import { ChevronRight, Target } from 'lucide-react';
import { Badge } from '../../ui';
import { optionLabel, GOAL_LEVELS } from '../../features/goals/goalConfig';
import {
  buildGoalTree,
  statusLabel,
  statusTone,
  type GoalTreeNode,
} from '../../features/goals/goalModel';
import type { GoalSummary } from '../../features/goals/types';

export interface GoalTreeViewProps {
  goals: GoalSummary[];
  onSelect: (goal: GoalSummary) => void;
}

function Row({
  node,
  onSelect,
}: {
  node: GoalTreeNode;
  onSelect: (goal: GoalSummary) => void;
}) {
  const g = node.goal;
  return (
    <>
      <button
        type="button"
        onClick={() => onSelect(g)}
        className="flex w-full items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition-colors hover:border-surface-border hover:bg-surface-3"
        style={{ paddingLeft: `${node.depth * 20 + 12}px` }}
      >
        {node.children.length > 0 ? (
          <ChevronRight className="h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
        ) : (
          <Target className="h-4 w-4 shrink-0 text-zinc-600" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-zinc-100">{g.title}</p>
          <p className="truncate text-xs text-zinc-500">
            {optionLabel(GOAL_LEVELS, g.level)}
            {g.owningTeam ? ` · ${g.owningTeam}` : ''}
          </p>
        </div>
        {g.manualProgress !== undefined && (
          <span className="hidden text-xs tabular-nums text-zinc-500 sm:inline">
            {g.manualProgress}%
          </span>
        )}
        <Badge tone={statusTone(g.status)} dot>
          {statusLabel(g.status)}
        </Badge>
      </button>
      {node.children.map((child) => (
        <Row key={child.goal.id} node={child} onSelect={onSelect} />
      ))}
    </>
  );
}

/**
 * Renders the goal hierarchy as an indented parent/child tree derived from
 * parentGoalId. Clicking a row opens the goal's detail. The forest is built by
 * {@link buildGoalTree} (cycle-safe, level-sorted).
 */
export function GoalTreeView({ goals, onSelect }: GoalTreeViewProps) {
  const forest = buildGoalTree(goals);
  return (
    <div className="rounded-2xl border border-surface-border bg-surface-1/40 p-2">
      {forest.map((node) => (
        <Row key={node.goal.id} node={node} onSelect={onSelect} />
      ))}
    </div>
  );
}

export default GoalTreeView;
